import React from "react";
import "./allDayEventStrip.css";

/**
 * All-day (personal "SIX") event strip — 2026-09-11.
 *
 * Renders between the day-header row and the scrolling hour grid on WeekModal
 * (7-day desktop + 3-day mobile) and DayViewModal. All-day items are anchored
 * at Central noon with no endMs and MUST never become hour blocks, so the
 * hosts split them out before lane layout and hand the per-column lists here.
 *
 * Column alignment contract: the strip reproduces the header's geometry —
 * a fixed `hourLabelWidth` spacer (the host's HOUR_LABEL_WIDTH), `flex: 1`
 * day cells, and the desktop `margin-right: 20px` scroll-gutter offset that
 * `.week-grid-header` carries (reset to 0 at <=900px; DayView resets it with
 * `single`). Drift any of those and the chips sit 20px off their columns.
 *
 * Props:
 *   days           Array<Array<item>> — one entry per visible column, each the
 *                  already-filtered + sorted all-day items for that day.
 *   hourLabelWidth px — must equal the host grid's HOUR_LABEL_WIDTH.
 *   colorFor       (item) => css colour (creator colour).
 *   onItemClick    (item) => void — hosts pass the same EventDetailPopup
 *                  opener their hour blocks use.
 *   single         DayView: kills the desktop gutter margin (!important, like
 *                  .day-view-single-header).
 *
 * Renders nothing when every column is empty, so surfaces without all-day
 * events are pixel-identical to before.
 */
const AllDayEventStrip = ({
  days,
  hourLabelWidth,
  colorFor,
  onItemClick,
  single = false,
}) => {
  const cols = Array.isArray(days) ? days : [];
  if (!cols.some((d) => Array.isArray(d) && d.length > 0)) return null;

  return (
    <div className={`all-day-strip${single ? " all-day-strip--single" : ""}`}>
      <div
        className="all-day-strip-spacer"
        style={{ minWidth: hourLabelWidth, width: hourLabelWidth }}
        aria-hidden="true"
      />
      {cols.map((dayItems, idx) => (
        <div key={idx} className="all-day-strip-cell">
          {(dayItems || []).map((item, i) => {
            const ci = item?.calendarItem || {};
            const label = ci.label || "";
            return (
              <button
                key={ci.id || ci._id || i}
                type="button"
                className="all-day-chip"
                style={{ backgroundColor: colorFor ? colorFor(item) : undefined }}
                title={label}
                onClick={(e) => {
                  e.stopPropagation();
                  onItemClick?.(item);
                }}
              >
                <span className="all-day-chip-label">{label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default AllDayEventStrip;
